import { size } from "lodash";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useLocalStorageState from "use-local-storage-state";

import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FullscreenExitOutlined from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlined from "@mui/icons-material/FullscreenOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { grey } from "@mui/material/colors";
import { useSelector } from "react-redux";
import UpdateLoanDocumentMetadataForm from "../../../forms/UpdateLoanDocumentMetadataForm";
import { nsKey } from "../../../utils/storage/partnerStorage";
import DocumentAssociations, {
  buildResolvedAssociations,
} from "../../associations/DocumentAssociations";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { useCommentUtilsHook } from "../../../hooks/utils/useCommentUtilsHook";
import CreateConditionFromDocButton from "../../common/Loan/buttons/CreateConditionFromDocButton";
import FilePreviewImageTypeIcon from "../../common/FilePreviewImageTypeIcon";
import LoanEnhancedCommentTextBox from "../../common/Loan/LoanEnhancedCommentTextBox";
import EnhancedCommentItemRenderer from "../../../_src_shared/comments/EnhancedCommentItemRenderer";
import { isSpreadsheetFile } from "../../common/isSpreadsheetFile";
import { EditOutlined } from "@mui/icons-material";
import colors from "../../../theme/colors";

// Heavy (xlsx + AG Grid) — only loaded when an Excel file is previewed.
const ExcelPreview = lazy(() => import("../../common/ExcelPreview"));

const LoanFilePreviewModal = ({
  file,
  loan,
  containerId,
  onClose,
  open,
  relatedFiles,
  onSelectRelatedFile,
}) => {
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Raw bytes for the spreadsheet parser (see ExcelPreview) — parsed directly
  // rather than by re-fetching a possibly-revoked object URL.
  const [previewBlob, setPreviewBlob] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  // Persisted so the user's fullscreen preference is remembered across opens.
  const [isFullscreen, setIsFullscreen] = useLocalStorageState(
    nsKey("loanFilePreviewModalFullscreen"),
    { defaultValue: false },
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const hasRelatedFiles = size(relatedFiles) > 1;
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, getFileById } = useLoanFilesHook();
  const { getFileAssociatedComments } = useCommentUtilsHook();

  const fileObject = useMemo(() => {
    const filtered = loanDrilldown?.loanDocuments?.filter(
      (d) => d._id === file?._id || d.oid === file?._id,
    );
    if (size(filtered) === 0) return file;
    return filtered[0];
  }, [file, loanDrilldown]);

  const targetLoan = useMemo(() => {
    if (loan) return loan;
    return loanDrilldown;
  }, [loanDrilldown, loan]);
  const content_type = fileObject?.content_type;
  const file_display_name = fileObject?.file_display_name;
  const file_storage_name = fileObject?.file_storage_name;
  const fileId = fileObject?._id;
  const loanId = containerId || fileObject?.dealId || fileObject?.containerName;

  const isImage = content_type?.startsWith("image/");
  const isPdf = content_type === "application/pdf";
  const isExcel = isSpreadsheetFile({
    content_type,
    fileName: file_display_name,
  });

  const relatedComments = getFileAssociatedComments(
    targetLoan?.comments,
    fileId,
  );

  const associationCount = useMemo(() => {
    const resolved = buildResolvedAssociations(targetLoan, fileId);
    return Object.values(resolved).reduce(
      (acc, ids) => acc + (Array.isArray(ids) ? ids.length : 0),
      0,
    );
  }, [targetLoan, fileId]);

  // ── Fetch file blob ──────────────────────────────────────────
  const fetchFile = useCallback(() => {
    if (!loanId || !file_storage_name) return;
    setError(null);

    getFileById({
      loanId,
      file_storage_name,
      isFile: true,
      onSuccessFn: (responseData) => {
        try {
          const blob =
            responseData instanceof Blob
              ? responseData
              : new Blob([responseData], { type: content_type });
          setPreviewBlob(blob);
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        } catch (err) {
          console.error("Error creating Blob URL:", err);
          setError(
            "Failed to process the file. The format may be unsupported.",
          );
        }
      },
      onErrorFn: (err) => {
        console.error("Error fetching loan file:", err);
        setError("Failed to load the file. Please try again.");
      },
    });
  }, [loanId, file_storage_name, content_type, getFileById]);

  useEffect(() => {
    if (open && fileObject && file_storage_name && loanId) {
      setActiveTab(0);

      fetchFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fileObject]);

  // Cleanup blob URL on unmount / URL change
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleClose = useCallback(() => {
    setPreviewUrl(null);
    setPreviewBlob(null);
    setError(null);
    setActiveTab(0);
    // Note: fullscreen is intentionally NOT reset here — it's a persisted
    // preference (see useLocalStorageState above).

    if (isFunction(onClose)) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const handleDownload = useCallback(() => {
    if (!loanId || !file_storage_name) return;
    getFileById({
      loanId,
      file_storage_name,
      isFile: true,
      fileDownload: true,
      fileDownloadName: file_display_name || file_storage_name,
    });
  }, [loanId, file_storage_name, file_display_name, getFileById]);

  const handleOpenInNewTab = useCallback(() => {
    if (!loanId || !file_storage_name) return;
    getFileById({
      loanId,
      file_storage_name,
      isFile: true,
      openInNewTab: true,
    });
  }, [loanId, file_storage_name, getFileById]);

  // ── Detail rows for the "Details" tab ────────────────────────
  const detailRows = useMemo(() => {
    if (isNil(fileObject)) return [];
    return [
      { label: "Document Group", value: fileObject?.docGroup },
      { label: "Document Type", value: fileObject?.docType },
      {
        label: "Status",
        value: fileObject?.documentStatus || fileObject?.status,
      },
      { label: "Content Type", value: content_type },
      { label: "Upload Date", value: fileObject?.uploadDate },
      { label: "Modified Date", value: fileObject?.modifiedDate },
      { label: "Uploaded By", value: fileObject?.uploadedBy },
    ].filter((row) => !isNil(row.value) && row.value !== "");
  }, [fileObject, content_type]);

  const paperSx = useMemo(() => {
    if (isFullscreen) {
      return {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh",
        maxHeight: "100vh",
        borderRadius: 0,
        margin: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease-in-out",
      };
    }
    return {
      // Wider default when a related-files sidebar needs to share the row
      // — without the bump, the 240px sidebar eats ~1/3 of the preview.
      width: hasRelatedFiles ? 1080 : 720,
      maxWidth: "95vw",
      height: "85vh",
      maxHeight: 860,
      borderRadius: "12px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      transition: "all 0.2s ease-in-out",
    };
  }, [isFullscreen, activeTab, hasRelatedFiles]);

  if (isNil(fileObject)) return null;

  return (
    <>
      <Dialog
        open={!!open}
        onClose={handleClose}
        maxWidth={false}
        fullScreen={isFullscreen}
        PaperProps={{ sx: paperSx }}
      >
        {/* ── Header ───────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            background: "#fff",
          }}
        >
          {/* Left: icon + file name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              minWidth: 0,
              flexGrow: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                background: grey[100],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FilePreviewImageTypeIcon contentType={content_type} size={22} />
            </Box>
            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <Tooltip title={file_display_name || "File Preview"}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file_display_name || "File Preview"}
                </Typography>
              </Tooltip>
              {(fileObject?.docGroup || fileObject?.docType) && (
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: grey[500],
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {[fileObject?.docGroup, fileObject?.docType]
                    .filter(Boolean)
                    .join(" › ")}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right: action buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              ml: 1,
              flexShrink: 0,
            }}
          >
            <CreateConditionFromDocButton
              doc={fileObject}
              variant="icon"
              onBeforeFullForm={handleClose}
            />
            <Tooltip title="Download">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in new tab">
              <IconButton size="small" onClick={handleOpenInNewTab}>
                <OpenInNewOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <IconButton
                size="small"
                onClick={() => setIsFullscreen((f) => !f)}
              >
                {isFullscreen ? (
                  <FullscreenExitOutlined sx={{ fontSize: 20 }} />
                ) : (
                  <FullscreenOutlined sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <IconButton size="small" onClick={handleClose}>
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Tabs ─────────────────────────────────────────────── */}
        <Box
          sx={{ borderBottom: 1, borderColor: "divider", background: "#fff" }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              minHeight: 36,
              px: 2,
              "& .MuiTab-root": {
                minHeight: 36,
                textTransform: "none",
                fontSize: "12.5px",
                fontWeight: 600,
                py: 0,
              },
              "& .MuiTabs-indicator": {
                // height: 2,
                borderRadius: 1,
              },
            }}
          >
            <Tab label="Preview" />
            <Tab label="Edit" />
            <Tab
              label={`Associations${associationCount ? ` (${associationCount})` : ""}`}
            />
            <Tab label={`Comments (${size(relatedComments)})`} />

            <Tab label="Metadata" />
          </Tabs>
        </Box>

        {/* ── Body: optional related-files sidebar + tab content ── */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {hasRelatedFiles && !sidebarCollapsed && (
            <RelatedFilesSidebar
              files={relatedFiles}
              activeFileId={fileObject?._id}
              onSelect={onSelectRelatedFile}
              onCollapse={() => setSidebarCollapsed(true)}
            />
          )}
          {hasRelatedFiles && sidebarCollapsed && (
            <Box
              sx={{
                width: 28,
                flexShrink: 0,
                borderRight: `1px solid ${grey[200]}`,
                background: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                pt: 1,
              }}
            >
              <Tooltip title="Show related files">
                <IconButton
                  size="small"
                  onClick={() => setSidebarCollapsed(false)}
                >
                  <ChevronRight sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              position: "relative",
              bgcolor: grey[50],
            }}
          >
            {/* Preview Tab */}
            {activeTab === 0 && (
              <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
                {/* Loading */}
                {loading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.85)",
                      zIndex: 2,
                    }}
                  >
                    <CircularProgress size={32} />
                    <Typography
                      sx={{ mt: 1.5, fontSize: "13px", color: grey[600] }}
                    >
                      Loading file…
                    </Typography>
                  </Box>
                )}

                {/* Error */}
                {!loading && error && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    <ErrorOutline color="error" sx={{ fontSize: 40 }} />
                    <Typography
                      color="error"
                      sx={{
                        mt: 1.5,
                        fontSize: "13px",
                        textAlign: "center",
                        px: 3,
                      }}
                    >
                      {error}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchFile}
                      sx={{ mt: 2 }}
                    >
                      Try Again
                    </Button>
                  </Box>
                )}

                {/* Image preview */}
                {!loading && !error && previewUrl && isImage && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      background: "#282828",
                      p: 2,
                    }}
                  >
                    <img
                      src={previewUrl}
                      alt={file_display_name || "Image preview"}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: 4,
                      }}
                    />
                  </Box>
                )}

                {/* PDF preview */}
                {!loading && !error && previewUrl && isPdf && (
                  <iframe
                    src={previewUrl}
                    title={file_display_name || "PDF preview"}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      position: "absolute",
                      inset: 0,
                    }}
                  />
                )}

                {/* Excel / spreadsheet preview */}
                {!loading && !error && previewUrl && isExcel && (
                  <Box sx={{ position: "absolute", inset: 0 }}>
                    <Suspense
                      fallback={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      }
                    >
                      <ExcelPreview
                        blob={previewBlob}
                        fileUrl={previewUrl}
                        fileName={file_display_name}
                        height="100%"
                      />
                    </Suspense>
                  </Box>
                )}

                {/* Unsupported type */}
                {!loading &&
                  !error &&
                  previewUrl &&
                  !isImage &&
                  !isPdf &&
                  !isExcel && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        p: 3,
                      }}
                    >
                      <FilePreviewImageTypeIcon
                        contentType={content_type}
                        size={48}
                      />
                      <Typography
                        sx={{
                          mt: 2,
                          fontSize: "13px",
                          color: grey[600],
                          textAlign: "center",
                        }}
                      >
                        This file type ({content_type}) cannot be previewed.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadOutlined />}
                        onClick={handleDownload}
                        sx={{ mt: 2 }}
                      >
                        Download File
                      </Button>
                    </Box>
                  )}
              </Box>
            )}

            {/* Modify Tab */}
            {activeTab === 1 && (
              <Box
                sx={{
                  p: 3,
                  overflowY: "auto",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #e5e8ee",
                    borderRadius: "14px",
                    boxShadow:
                      "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: 680,
                    margin: "0 auto",
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",

                      alignItems: "flex-start",
                      gap: 1.5,
                      p: "20px 24px",
                      borderBottom: "1px solid #eef1f5",
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "9px",
                        background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
                        border: "1px solid #e6e0fb",
                        color: "#7c3aed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <EditOutlined sx={{ fontSize: 17 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          letterSpacing: "-0.01em",
                          color: "#0f172a",
                          display: "block",
                        }}
                      >
                        Edit Document Metadata
                      </Box>
                      <Box
                        sx={{
                          fontSize: 12.5,
                          color: "#64748b",
                          lineHeight: 1.5,
                          display: "block",
                          mt: "3px",
                        }}
                      >
                        Edit the metadata for this document. Changes will be
                        saved immediately.
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ p: "20px 24px" }}>
                    <UpdateLoanDocumentMetadataForm
                      file={fileObject}
                      loanId={loanId}
                      onSuccessFn={() => {
                        fetchFile();
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Associations Tab */}
            {activeTab === 2 && (
              <Box sx={{ p: 3, overflowY: "auto", height: "100%" }}>
                <DocumentAssociations
                  doc={fileObject}
                  loan={targetLoan}
                  loanId={loanId}
                />
              </Box>
            )}

            {/* Comments Tab */}
            {activeTab === 3 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {/* Comment input - fixed at top */}
                <Box
                  sx={{
                    flexShrink: 0,
                    p: 3,
                    pb: 1,
                    borderBottom: `1px solid ${grey[200]}`,
                    background: "#fff",
                  }}
                >
                  <LoanEnhancedCommentTextBox
                    loan={targetLoan}
                    documentIds={[fileId]}
                    sx={{
                      border: `1px solid ${grey[300]}`,
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                  />
                </Box>

                {/* Comment list - scrollable */}
                <Box
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                  }}
                >
                  <EnhancedCommentItemRenderer
                    loan={targetLoan}
                    comments={relatedComments}
                    padding={2}
                    sx={{ width: "100%" }}
                  />
                </Box>
              </Box>
            )}

            {/* Metadata Tab */}
            {activeTab === 4 && (
              <Box sx={{ p: 3, overflowY: "auto", height: "100%" }}>
                {detailRows.map(({ label, value }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.25,
                      borderBottom: `1px solid ${grey[200]}`,
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: grey[500],
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: grey[800],
                        fontWeight: 500,
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

/**
 * Clickable list of related files shown to the left of the modal body.
 * Lifts clicks up via `onSelect(file)` so the parent `BasicFilesTable`
 * can flip its `file` state — the modal's existing effects re-fetch on
 * prop change, so no internal file state is needed here.
 */
const RelatedFilesSidebar = ({ files, activeFileId, onSelect, onCollapse }) => {
  // Scroll the highlighted file into view when it changes (e.g. opening a file
  // that sits far down the list) so the selection is never off-screen.
  const activeRef = useRef(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeFileId]);

  if (!files?.length || !isFunction(onSelect)) return null;
  return (
    <Box
      sx={{
        width: 260,
        flexShrink: 0,
        borderRight: `1px solid ${grey[200]}`,
        background: "#fff",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 0.75,
          borderBottom: `1px solid ${grey[100]}`,
          position: "sticky",
          top: 0,
          background: "#fff",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "10px",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: grey[500],
            fontWeight: 600,
          }}
        >
          Related Files ({files.length})
        </Typography>
        {isFunction(onCollapse) && (
          <Tooltip title="Hide related files">
            <IconButton size="small" onClick={onCollapse}>
              <ChevronLeft sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {files.map((f) => {
        const isActive = f?._id === activeFileId;
        return (
          <Box
            key={f?._id || f?.file_storage_name}
            ref={isActive ? activeRef : undefined}
            onClick={() => onSelect(f)}
            sx={{
              px: 2,
              py: 1.25,
              cursor: "pointer",
              borderBottom: `1px solid ${grey[100]}`,
              background: isActive ? "#e8f0ff" : "transparent",
              borderLeft: isActive
                ? `3px solid ${colors.brand}`
                : "3px solid transparent",
              "&:hover": {
                background: isActive ? "#e8f0ff" : grey[50],
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilePreviewImageTypeIcon
                contentType={f?.content_type}
                size={16}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: isActive ? 700 : 500,
                  color: grey[900],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {f?.file_display_name || f?.file_storage_name || "Untitled"}
              </Typography>
            </Box>
            {(f?.docGroup || f?.docType) && (
              <Typography
                sx={{
                  fontSize: "10.5px",
                  color: grey[500],
                  mt: 0.25,
                  ml: 3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {[f?.docGroup, f?.docType].filter(Boolean).join(" › ")}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default LoanFilePreviewModal;
